import { useCallback, type RefObject } from 'react';
import type { QueryExecutionError } from '../../../../types/queryExecution';
import type { QueryEditorHandle } from '../components/QueryEditor';

type UseRuntimeErrorMarkerParams = {
  editorRef: RefObject<QueryEditorHandle | null>;
};

/**
 * Places or clears Monaco runtime-error squiggles based on the structured
 * errorLocation returned by the engine adapter after a failed execution.
 * Uses a distinct owner ('sql-runtime-error') so these markers don't
 * interfere with IntelliSense's edit-time diagnostics ('sql' owner).
 * Behavior carried verbatim from the previous inline implementation.
 */
export function useRuntimeErrorMarker({ editorRef }: UseRuntimeErrorMarkerParams) {
  const handleErrorLocation = useCallback(
    (loc: QueryExecutionError['errorLocation'] | undefined, message?: string) => {
      const editor = editorRef.current;
      if (!editor) return;
      if (!loc) {
        // Success or no location info — clear any stale runtime-error marker.
        editor.setMarkers([], 'sql-runtime-error');
        return;
      }
      // errorLocation uses 0-indexed line/column; Monaco expects 1-indexed.
      const monacoLine = loc.line + 1;
      const monacoCol = loc.column > 0 ? loc.column + 1 : 1;
      // When column is 0 (engine doesn't provide column, e.g. MySQL), highlight
      // to end-of-line; otherwise highlight a single token width from the column.
      const markerEndCol = loc.column > 0 ? monacoCol + 1 : 999;
      editor.setMarkers(
        [
          {
            startLineNumber: monacoLine,
            startColumn: monacoCol,
            endLineNumber: monacoLine,
            endColumn: markerEndCol,
            message: message ?? '',
            severity: 'error',
          },
        ],
        'sql-runtime-error',
      );
    },
    // editorRef is a stable ref object — no reactive deps needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { handleErrorLocation };
}
